import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, Loader2, Trash2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import { ellieArtifactService, type EllieArtifact } from '@/services/ellieArtifact.service';

mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
});

// Mermaid code block renderer
function MermaidDiagram({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const id = `mermaid-${Math.random().toString(36).slice(2, 9)}`;
    mermaid.render(id, code).then(({ svg }) => {
      if (containerRef.current) {
        containerRef.current.innerHTML = svg;
      }
    }).catch((err) => {
      if (containerRef.current) {
        containerRef.current.innerHTML = `<pre class="text-destructive text-sm">Mermaid error: ${err.message}</pre>`;
      }
    });
  }, [code]);

  return <div ref={containerRef} className="my-4 flex justify-center" />;
}

// Markdown components with mermaid support
const remarkPlugins = [remarkGfm];

const markdownComponents = {
  h1: ({ className, ...props }: any) => (
    <h1 className={cn('text-2xl font-bold mb-4 first:mt-0 mt-6', className)} {...props} />
  ),
  h2: ({ className, ...props }: any) => (
    <h2 className={cn('text-xl font-bold mb-3 first:mt-0 mt-5', className)} {...props} />
  ),
  h3: ({ className, ...props }: any) => (
    <h3 className={cn('text-lg font-semibold mb-2 first:mt-0 mt-4', className)} {...props} />
  ),
  h4: ({ className, ...props }: any) => (
    <h4 className={cn('text-base font-semibold mb-2 first:mt-0 mt-3', className)} {...props} />
  ),
  p: ({ className, ...props }: any) => (
    <p className={cn('mb-4 last:mb-0', className)} {...props} />
  ),
  a: ({ className, ...props }: any) => (
    <a className={cn('text-primary underline hover:text-primary/80', className)} {...props} />
  ),
  blockquote: ({ className, ...props }: any) => (
    <blockquote className={cn('border-l-4 border-border pl-4 italic my-4', className)} {...props} />
  ),
  ul: ({ className, ...props }: any) => (
    <ul className={cn('list-disc mb-4 space-y-1 pl-6', className)} {...props} />
  ),
  ol: ({ className, ...props }: any) => (
    <ol className={cn('list-decimal mb-4 space-y-1 pl-6', className)} {...props} />
  ),
  li: ({ className, ...props }: any) => (
    <li className={cn('pl-2', className)} {...props} />
  ),
  hr: ({ className, ...props }: any) => (
    <hr className={cn('my-4 border-border', className)} {...props} />
  ),
  table: ({ className, ...props }: any) => (
    <table className={cn('w-full border-collapse border border-border my-4', className)} {...props} />
  ),
  th: ({ className, ...props }: any) => (
    <th className={cn('border border-border px-4 py-2 bg-muted font-semibold text-left', className)} {...props} />
  ),
  td: ({ className, ...props }: any) => (
    <td className={cn('border border-border px-4 py-2', className)} {...props} />
  ),
  tr: ({ className, ...props }: any) => (
    <tr className={cn('', className)} {...props} />
  ),
  code: ({ className, children, ...props }: any) => {
    // Check if this is a mermaid code block (inside a <pre>)
    const match = /language-mermaid/.exec(className || '');
    if (match) {
      return <MermaidDiagram code={String(children).replace(/\n$/, '')} />;
    }
    return (
      <code className={cn('bg-muted px-1.5 py-0.5 rounded text-sm font-mono', className)} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }: any) => {
    // If the child is a mermaid code block, render without the <pre> wrapper
    const child = Array.isArray(children) ? children[0] : children;
    if (child?.props?.className?.includes('language-mermaid')) {
      return <>{children}</>;
    }
    return (
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4 [&>code]:block [&>code]:p-0 [&>code]:bg-transparent" {...props}>
        {children}
      </pre>
    );
  },
};

function ArtifactDetail({ artifact, onBack }: { artifact: EllieArtifact; onBack: () => void }) {
  return (
    <div className="max-w-4xl mx-auto">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Artifacts
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>{artifact.name}</CardTitle>
          {artifact.description && (
            <CardDescription>{artifact.description}</CardDescription>
          )}
          <p className="text-xs text-muted-foreground">
            Created {new Date(artifact.created_at).toLocaleDateString()} at{' '}
            {new Date(artifact.created_at).toLocaleTimeString()}
          </p>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
              {artifact.markdown}
            </ReactMarkdown>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ArtifactsPage() {
  const queryClient = useQueryClient();
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ellieArtifacts'],
    queryFn: async () => {
      return await ellieArtifactService.find({ pageSize: 100 });
    },
    staleTime: 30 * 1000,
  });

  const artifacts = data?.records || [];
  const selectedArtifact = artifacts.find((a) => a.id === selectedArtifactId);

  const handleDelete = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleting(id);
    try {
      await ellieArtifactService.delete(id);
      showToast.success('Artifact deleted');
      if (selectedArtifactId === id) setSelectedArtifactId(null);
      queryClient.invalidateQueries({ queryKey: ['ellieArtifacts'] });
    } catch (err: any) {
      showToast.error('Failed to delete artifact', { description: err.message });
    } finally {
      setDeleting(null);
    }
  }, [selectedArtifactId, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (selectedArtifact) {
    return (
      <div className="p-8 h-full overflow-auto">
        <ArtifactDetail artifact={selectedArtifact} onBack={() => setSelectedArtifactId(null)} />
      </div>
    );
  }

  return (
    <div className="p-8 h-full overflow-auto">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Artifacts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reports, analyses, and documents generated by Ellie.
          </p>
        </div>

        {artifacts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">
                No artifacts yet. Ask Ellie to generate a report or write up her findings.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {artifacts.map((artifact) => (
              <Card
                key={artifact.id}
                className="cursor-pointer transition-colors hover:bg-accent"
                onClick={() => setSelectedArtifactId(artifact.id)}
              >
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{artifact.name}</p>
                      {artifact.description && (
                        <p className="text-sm text-muted-foreground truncate">{artifact.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(artifact.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleDelete(artifact.id, e)}
                    disabled={deleting === artifact.id}
                  >
                    {deleting === artifact.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-destructive" />
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
